import React, { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'

// Layouts
import MainLayout from './layouts/MainLayout'

// Contexts & Config
import { UserProvider, useUser } from './contexts/UserContext'
import { ThemeProvider, useTheme } from './contexts/ThemeProvider'
import { CRISP_CONFIG } from './config/crisp'

// 路由级代码分割 - 使用 React.lazy() 按需加载页面组件
// 这样可以显著减少初始 bundle 大小，提升首屏加载速度
const Dashboard = lazy(() => import('./features/dashboard/Dashboard'))
const Login = lazy(() => import('./features/auth/Login'))
const Register = lazy(() => import('./features/auth/Register'))
const ForgotPassword = lazy(() => import('./features/auth/ForgotPassword'))
const TrafficChart = lazy(() => import('./features/traffic/TrafficChart'))
const NotificationPage = lazy(() => import('./features/notifications/NotificationPage'))
const ServerListPage = lazy(() => import('./features/servers/ServerListPage'))
const Profile = lazy(() => import('./features/profile/Profile'))
const KnowledgeDocsPage = lazy(() => import('./features/knowledge/KnowledgeDocsPage'))
const KnowledgeDocDetailPage = lazy(() => import('./features/knowledge/KnowledgeDocDetailPage'))
const PlansPage = lazy(() => import('./features/plans/PlansPage'))
const CheckoutPage = lazy(() => import('./features/plans/CheckoutPage'))
const OrdersPage = lazy(() => import('./features/orders/OrdersPage'))
const OrderDetailPage = lazy(() => import('./features/orders/OrderDetailPage'))
const InvitePage = lazy(() => import('./features/invite/InvitePage'))
const TicketPage = lazy(() => import('./features/tickets/TicketPage'))
const CreateTicketPage = lazy(() => import('./features/tickets/CreateTicketPage'))
const TicketDetailPage = lazy(() => import('./features/tickets/TicketDetailPage'))
const NexGenLanding = lazy(() => import('./features/landing/NexGenLanding'))
const HongLinkLanding = lazy(() => import('./features/landing/HongLinkLanding'))

// Common Components
import CrispChat from './components/common/CrispChat'
// 页面过渡优化样式
import './styles/page-transition.css'
import { Spinner } from '@heroui/react'

// 懒加载 Suspense 回退组件
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <Spinner size="lg" />
  </div>
)

// 受保护的路由组件
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, isLoading } = useUser()
  const location = useLocation()

  // 关键优化：只有在 未登录 且 正在加载 时才显示 Loading
  // 如果用户已经登录，即使后台在刷新数据（isLoading=true），也不要替换整个 Layout，防止闪烁
  if (isLoading && !isLoggedIn) {
    return (
      <div className="flex items-center justify-center min-h-dvh bg-background">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!isLoggedIn && !isLoading) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return children
}

// 公共路由组件（已登录用户重定向到仪表板）
const PublicRoute = ({ children }) => {
  const { isLoggedIn, isLoading } = useUser()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-dvh bg-background">
        <Spinner size="lg" />
      </div>
    )
  }

  if (isLoggedIn) {
    const from = location.state?.from?.pathname || '/dashboard'
    return <Navigate to={from} replace />
  }

  return children
}

const LandingRoute = () => {
  const [landingConfig, setLandingConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/landing.json')
      .then(res => res.json())
      .then(data => {
        setLandingConfig(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load landing config:', err);
        setLandingConfig({ current: 'nexgen' }); // Default to NexGen if fetch fails
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-dvh bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (landingConfig?.current === 'hong-link') {
    return <PublicRoute><HongLinkLanding /></PublicRoute>;
  }

  return <PublicRoute><NexGenLanding /></PublicRoute>;
};

const AppContent = () => {
  const [appConfig, setAppConfig] = useState(null)
  const { isDark } = useTheme()

  useEffect(() => {
    fetch('/app.json')
      .then(res => res.json())
      .then(data => {
        setAppConfig(data)
      })
      .catch(err => console.error('Failed to load app config:', err))
  }, [])

  // ============================================================
  // Toaster 样式 —— 玻璃质感中性卡片 + 3px 彩色左边框 + 左上彩色光晕渗染
  // 自适应 light/dark；overrides via appConfig?.theme?.successBg/errorBg 仍生效
  // ============================================================
  const toastBase = {
    padding: '12px 14px 12px 16px',
    minWidth: '240px',
    maxWidth: '420px',
    borderRadius: '14px',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.45,
    letterSpacing: '0.01em',
    wordBreak: 'break-word',
    backdropFilter: 'blur(14px) saturate(160%)',
    WebkitBackdropFilter: 'blur(14px) saturate(160%)',
    background: isDark ? 'rgba(24, 24, 27, 0.82)' : 'rgba(255, 255, 255, 0.90)',
    color: isDark ? '#F4F4F5' : '#18181B',
    border: isDark
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: isDark
      ? '0 12px 32px -12px rgba(0, 0, 0, 0.55), 0 4px 10px -2px rgba(0, 0, 0, 0.35)'
      : '0 12px 32px -12px rgba(15, 23, 42, 0.18), 0 4px 10px -2px rgba(15, 23, 42, 0.06)',
  }

  // 构造 "中性玻璃底 + 彩色光晕渗染 + 彩色左边框 + 染色阴影"
  // 关键：linear-gradient (透明度极淡) 叠在中性纯色之上，玻璃质感和文字对比度都不丢
  const accent = (rgb, override) => {
    const tintStrong = isDark ? 0.22 : 0.14   // 左上角浓度
    const tintMid    = isDark ? 0.08 : 0.05   // 中段
    const baseBg     = isDark ? 'rgba(24, 24, 27, 0.82)' : 'rgba(255, 255, 255, 0.90)'

    return {
      ...toastBase,
      background: override
        ? override
        : `linear-gradient(135deg, rgba(${rgb}, ${tintStrong}) 0%, rgba(${rgb}, ${tintMid}) 55%, rgba(${rgb}, 0) 100%), ${baseBg}`,
      borderLeft: `3px solid rgb(${rgb})`,
      boxShadow: isDark
        ? `0 12px 32px -12px rgba(${rgb}, 0.32), 0 4px 10px -2px rgba(0, 0, 0, 0.4)`
        : `0 12px 32px -12px rgba(${rgb}, 0.32), 0 4px 10px -2px rgba(15, 23, 42, 0.06)`,
    }
  }

  return (
    <div className="min-h-dvh bg-background transition-colors duration-300">
      <CrispChat
        websiteId={CRISP_CONFIG.websiteId}
        enabled={CRISP_CONFIG.enabled}
        autoLoad={CRISP_CONFIG.autoLoad}
      />

      <Toaster
        position="top-right"
        gutter={10}
        containerStyle={{
          top: 'calc(env(safe-area-inset-top) + 72px + 8px)',
          right: 'calc(env(safe-area-inset-right) + 16px)',
        }}
        toastOptions={{
          duration: 3000,
          style: toastBase,
          success: {
            duration: 2500,
            iconTheme: {
              primary: '#10B981',
              secondary: isDark ? '#0B0B0E' : '#FFFFFF',
            },
            style: accent('16, 185, 129', appConfig?.theme?.successBg),
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#EF4444',
              secondary: isDark ? '#0B0B0E' : '#FFFFFF',
            },
            style: accent('239, 68, 68', appConfig?.theme?.errorBg),
          },
          loading: {
            iconTheme: {
              primary: '#3B82F6',
              secondary: isDark ? '#0B0B0E' : '#FFFFFF',
            },
            style: accent('59, 130, 246'),
          },
        }}
      />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* 公共路由 */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/" element={<LandingRoute />} />

          {/* 
            终极优化方案：Shared Layout (嵌套路由)
            MainLayout 作为一个持久父组件，只在初始化时加载一次。
            切换子路由时，Layout 不会被销毁，只有 Outlet 内容会变化。
            这是解决"页面重新加载感"的唯一架构级方案。
          */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Suspense fallback={<PageLoader />}>
                    <Outlet />
                  </Suspense>
                </MainLayout>
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/traffic" element={<TrafficChart />} />
            <Route path="/notifications" element={<NotificationPage />} />
            <Route path="/servers" element={<ServerListPage />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/docs" element={<KnowledgeDocsPage />} />
            <Route path="/docs/:docId" element={<KnowledgeDocDetailPage />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/plans/checkout" element={<CheckoutPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:tradeNo" element={<OrderDetailPage />} />
            <Route path="/invite" element={<InvitePage />} />
            <Route path="/tickets" element={<TicketPage />} />
            <Route path="/tickets/create" element={<CreateTicketPage />} />
            <Route path="/tickets/:ticketId" element={<TicketDetailPage />} />
          </Route>

          {/* 404 & Redirect */}
          <Route path="*" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
        </Routes>
      </Suspense>
    </div>
  )
}

function App() {
  return (
    <UserProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </UserProvider>
  )
}

export default App
